import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { EllipsisLoader } from '@/components/ui/ellipsis-loader';
import { cn } from '@/lib/utils';
import { ChatMessage } from '@/machines/chatMachine.types';
import { FeedbackType } from '@/apiTypes/AiTypes';
import { AnimatedMessage } from './AnimatedMessage';
import { MessageFeedback } from './MessageFeedback';
import { MessageContent } from './MessageContent/MessageContent';
import zigbotAvatar from '@/assets/avatars/zigbot-avatar.svg';

interface ChatBubbleProps {
    message: ChatMessage;
    userAvatarSrc?: string;
    initials: string;
    animationComplete: boolean;
    onAnimationComplete: (messageId: string) => void;
    placeholderMap?: Record<string, string>;
    messageFeedback?: { type: FeedbackType; id: string };
    onFeedbackSubmitted: (messageId: string, feedbackType: FeedbackType, feedbackId: string) => void;
}

export const ChatBubble = ({
    message: msg,
    userAvatarSrc,
    initials,
    animationComplete,
    onAnimationComplete,
    placeholderMap,
    messageFeedback: feedback,
    onFeedbackSubmitted,
}: ChatBubbleProps) => {
    return (
        <div
            // The message itself is masked in session replay; its id lets a replay be traced to the stored message.
            data-replay-ref={msg.serverMessageId}
            className={cn(
                'flex gap-3 max-w-[85%]',
                msg.role === 'user' ? 'ml-auto flex-row-reverse' : 'mr-auto',
            )}
        >
            <Avatar
                className={cn(
                    'size-8 shrink-0',
                    msg.role === 'assistant'
                    && 'light:bg-gz-surface-medium dark:bg-gz-blue-600',
                )}
                aria-label={msg.role === 'assistant' ? 'Assistant' : undefined}
            >
                {msg.role === 'user'
                    ? (
                            <>
                                <AvatarImage src={userAvatarSrc} />
                                <AvatarFallback className="text-xs">
                                    {initials || 'U'}
                                </AvatarFallback>
                            </>
                        )
                    : (
                            <>
                                <AvatarImage
                                    src={zigbotAvatar}
                                    alt=""
                                    className="object-contain"
                                />
                                <AvatarFallback className="text-xs light:text-gz-neutral-800 dark:text-gz-neutral-white light:bg-gz-surface-medium dark:bg-gz-blue-500">
                                    ZB
                                </AvatarFallback>
                            </>
                        )}
            </Avatar>
            <div className="flex flex-col">
                <div
                    className={cn(
                        // Users type personal details into the chat, and replies echo them.
                        'rr-block rounded-lg px-4 text-sm',
                        msg.role === 'user'
                            ? 'bg-primary text-primary-foreground py-2'
                            : 'light:bg-gz-surface-medium dark:bg-gz-blue-900 text-foreground',
                        msg.role === 'assistant' && (msg.isStreaming && !msg.content ? 'py-3' : 'py-3'),
                    )}
                >
                    {msg.isStreaming && !msg.content
                        ? <EllipsisLoader />
                        : msg.role === 'assistant'
                            ? (
                                    <AnimatedMessage
                                        content={msg.content}
                                        isStreaming={msg.isStreaming ?? false}
                                        onAnimationComplete={() => onAnimationComplete(msg.id)}
                                        placeholderMap={placeholderMap}
                                    />
                                )
                            : <div className="whitespace-pre-wrap"><MessageContent content={msg.content} placeholderMap={placeholderMap} /></div>}
                </div>
                {msg.role === 'assistant' && !msg.isStreaming && msg.content && msg.serverMessageId && animationComplete && (
                    <MessageFeedback
                        messageId={msg.serverMessageId}
                        submittedFeedback={feedback}
                        onFeedbackSubmitted={(feedbackType, feedbackId) =>
                            onFeedbackSubmitted(msg.serverMessageId!, feedbackType, feedbackId)}
                    />
                )}
            </div>
        </div>
    );
};
